import { BaseService } from './base';

export type ConnectorType = 'magento' | 'upload';

// Helper to notify sidebar when connectors change
export const notifyConnectorsChanged = () => {
  window.dispatchEvent(new CustomEvent('connectors-changed'));
};

export interface Store {
  id: string;
  name: string;
  connector_type: ConnectorType;
  url: string;
  consumer_key: string;
  access_token?: string;
  magento_version: string;
  status: 'connected' | 'disconnected' | 'error' | 'testing' | 'syncing';
  last_sync?: string;
  connection_tested_at?: string;
  last_error?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateStoreRequest {
  name: string;
  connector_type?: ConnectorType;
  url?: string;
  consumer_key?: string;
  consumer_secret?: string;
  access_token?: string;
  access_token_secret?: string;
  api_token?: string;
  magento_version?: '2.3' | '2.4';
}

export interface UpdateStoreRequest {
  name?: string;
  connector_type?: ConnectorType;
  url?: string;
  consumer_key?: string;
  consumer_secret?: string;
  access_token?: string;
  access_token_secret?: string;
  api_token?: string;
  magento_version?: '2.3' | '2.4';
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  error_code?: string;
  store_info?: {
    name: string;
    version: string;
    base_currency: string;
    locale?: string;
    store_count?: number;
  };
}

export interface SyncStoreResponse {
  success: boolean;
  message: string;
  job_id: string;
  total_products: number;
  estimated_time_minutes: number;
}

export interface SyncJob {
  id: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  total_products: number;
  processed_products: number;
  current_page: number;
  progress_percentage: number;
  error_message?: string;
  estimated_remaining_minutes?: number;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface SyncStatusResponse {
  success: boolean;
  sync_job: SyncJob | null;
}

export interface CancelSyncResponse {
  success: boolean;
  message: string;
  job_id: string;
}

export interface DeleteAllProductsResponse {
  success: boolean;
  message: string;
  deleted_count: number;
}

export interface RecentSyncJob {
  id: string;
  store_id: string;
  store_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  total_products: number;
  processed_products: number;
  current_page: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  error_message?: string;
}

export interface RecentSyncJobsResponse {
  success: boolean;
  sync_jobs: RecentSyncJob[];
  message?: string;
}

export interface StoreView {
  code: string;
  name: string;
  id: number;
  is_active: boolean;
}

export interface StoreViewsResponse {
  store_views: StoreView[];
  fallback?: boolean;
  message?: string;
}

// Dashboard stats - single API call for all dashboard data
export interface DashboardStats {
  totalStores: number;
  connectedStores: number;
  totalProducts: number;
  activeSyncJobs: number;
  lastSyncDate: string | null;
  storeStats: Array<{
    id: string;
    name: string;
    status: string;
    productCount: number;
    lastSync?: string;
  }>;
  recentSyncJobs: RecentSyncJob[];
}

export class StoreService extends BaseService {
  private readonly endpoint = '/stores';

  async getStores(): Promise<Store[]> {
    const response = await this.makeFullRequest<Store[]>(this.endpoint);
    return response.data || [];
  }

  async getStore(id: string): Promise<Store> {
    // Backend returns direct store object (not wrapped)
    return this.makeRequest<Store>(`${this.endpoint}/${id}`);
  }

  async createStore(data: CreateStoreRequest): Promise<Store> {
    // Backend uses created() which returns { success: true, message: '...', data: store }
    const response = await this.makeRequest<Store>(this.endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  async updateStore(id: string, data: UpdateStoreRequest): Promise<Store> {
    // Backend uses success(res, { ...store, ... }) so BaseService should extract data correctly
    const response = await this.makeRequest<Store>(`${this.endpoint}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response;
  }

  async deleteStore(id: string): Promise<void> {
    await this.makeRequest(`${this.endpoint}/${id}`, {
      method: 'DELETE',
    });
  }

  async testConnection(data: CreateStoreRequest): Promise<TestConnectionResponse> {
    const response = await this.makeRequest<TestConnectionResponse>(`${this.endpoint}/test-connection`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  async testExistingStoreConnection(id: string): Promise<TestConnectionResponse> {
    const response = await this.makeRequest<TestConnectionResponse>(`${this.endpoint}/${id}/test-connection`, {
      method: 'POST',
    });
    return response;
  }

  async syncStore(id: string, storeViewCode: string = 'default', inventorySourceCode: string = 'online_wh'): Promise<SyncStoreResponse> {
    const response = await this.makeRequest<SyncStoreResponse>(`${this.endpoint}/${id}/sync`, {
      method: 'POST',
      body: JSON.stringify({
        store_view_code: storeViewCode,
        inventory_source_code: inventorySourceCode
      }),
    });
    return response;
  }

  async updateStock(id: string, inventorySourceCode: string = 'online_wh'): Promise<{ job_id?: string; message: string; updated_count?: number; estimated_time?: string }> {
    const response = await this.makeRequest<{ job_id?: string; message: string; updated_count?: number; estimated_time?: string }>('/utils/update-stock', {
      method: 'POST',
      body: JSON.stringify({
        store_id: id,
        inventory_source_code: inventorySourceCode
      }),
    });
    return response;
  }

  async getSyncStatus(id: string): Promise<SyncStatusResponse> {
    // Backend returns { success: true, data: { sync_job: {...} } } format
    // But the makeRequest method in BaseService already extracts the data portion
    // So we just need to get the sync_job directly
    const data = await this.makeRequest<{ sync_job: SyncJob | null }>(`${this.endpoint}/${id}/sync/status`);
    return {
      success: true,
      sync_job: data.sync_job
    };
  }

  async cancelSync(id: string): Promise<CancelSyncResponse> {
    const response = await this.makeRequest<CancelSyncResponse>(`${this.endpoint}/${id}/sync/cancel`, {
      method: 'POST',
    });
    return response;
  }

  async deleteAllProducts(id: string): Promise<DeleteAllProductsResponse> {
    const response = await this.makeRequest<DeleteAllProductsResponse>(`${this.endpoint}/${id}/products`, {
      method: 'DELETE',
    });
    return response;
  }

  async getRecentSyncJobs(limit: number = 5): Promise<RecentSyncJob[]> {
    const response = await this.makeRequest<{ sync_jobs: RecentSyncJob[] }>(`${this.endpoint}/recent-sync-jobs?limit=${limit}`);
    return response.sync_jobs || [];
  }

  // Get all dashboard stats in ONE API call (performance optimized)
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.makeRequest<DashboardStats>(`${this.endpoint}/dashboard-stats`);
    return response;
  }

  async getStoreViews(id: string): Promise<StoreViewsResponse> {
    const response = await this.makeRequest<StoreViewsResponse>(`${this.endpoint}/${id}/store-views`);
    return response;
  }

  async resumeSync(id: string): Promise<SyncStoreResponse> {
    const response = await this.makeRequest<SyncStoreResponse>(`${this.endpoint}/${id}/sync/resume`, {
      method: 'POST',
    });
    return response;
  }
}

export const storeService = new StoreService();